import { useCallback, useEffect, useState } from "react";
import {
  Activity,
  ArrowLeft,
  ExternalLink,
  HeartPulse,
  RefreshCw,
  X,
} from "lucide-react";
import { BloomMenu } from "../motion/bloom-menu";
import { useBackend } from "../../lib/backend";
import type { PsychSetupStatus, WearableProvider } from "../../lib/services";

type SetupStep = "choose" | "approval";

export function PsychAssistSetupFlow() {
  const backend = useBackend();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [status, setStatus] = useState<PsychSetupStatus | null>(null);
  const [busyProvider, setBusyProvider] = useState<WearableProvider | null>(
    null,
  );
  const [message, setMessage] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] =
    useState<WearableProvider | null>(null);
  const [step, setStep] = useState<SetupStep>("choose");

  const refresh = useCallback(async () => {
    const next = await backend.psych.getSetupStatus();
    setStatus(next);
    return next;
  }, [backend]);

  const close = () => {
    setOpen(false);
    setMessage(null);
    setSelectedProvider(null);
    setStep("choose");
  };

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ anchor?: unknown }>).detail;
      const trigger = detail?.anchor;
      setAnchor(
        trigger instanceof HTMLElement
          ? trigger
          : document.activeElement instanceof HTMLElement
            ? document.activeElement
            : document.querySelector<HTMLElement>(
                "[data-psychassist-setup-anchor]",
              ),
      );
      setOpen(true);
      void refresh();
    };
    window.addEventListener("fintheon:psychassist-setup-required", handler);
    return () =>
      window.removeEventListener(
        "fintheon:psychassist-setup-required",
        handler,
      );
  }, [refresh]);

  const connect = async (provider: WearableProvider) => {
    setBusyProvider(provider);
    setMessage(null);
    try {
      const next = await backend.psych.startWearableOAuth(provider);
      window.open(next.authUrl, "psychassist-wearable", "width=520,height=720");
      setSelectedProvider(provider);
      setStep("approval");
      setMessage(
        "Finish the provider approval, then check the connection here.",
      );
      window.setTimeout(() => void refresh(), 4000);
    } catch {
      setMessage("Provider OAuth is not configured yet.");
    } finally {
      setBusyProvider(null);
    }
  };

  const recheck = async () => {
    try {
      const next = await refresh();
      if (next.connections.some((item) => item.status === "connected")) {
        close();
        return;
      }
      setMessage(
        "The approval is still pending. Finish it in the provider window.",
      );
    } catch {
      setMessage("Connection status is unavailable. Try again in a moment.");
    }
  };

  const skip = async () => {
    await backend.psych.completeSetup({
      status: "skipped",
      source: "onboarding",
    });
    await refresh();
    close();
  };

  const providers = status?.providers ?? [];
  const connected = status?.connections.filter(
    (item) => item.status === "connected",
  );

  return (
    <BloomMenu
      open={open}
      onClose={close}
      anchor={anchor}
      ariaLabel="PsychAssist wearable setup"
      className="flex min-h-0 flex-col"
    >
      <div className="flex items-center justify-between px-3 py-2">
        <span className="inline-flex items-center gap-2 text-[11px] font-medium text-[var(--fintheon-text)]">
          <HeartPulse className="h-3.5 w-3.5 text-[var(--fintheon-accent)]" />
          PsychAssist wearable
        </span>
        <button
          type="button"
          aria-label="Close wearable setup"
          onClick={close}
          className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-[var(--fintheon-muted)] transition-colors hover:text-[var(--fintheon-text)]"
        >
          <X size={14} />
        </button>
      </div>
      {step === "approval" ? (
        <ApprovalStep
          provider={selectedProvider}
          message={message}
          onBack={() => {
            setStep("choose");
            setMessage(null);
          }}
          onRecheck={() => void recheck()}
        />
      ) : (
        <ProviderStep
          providers={providers}
          connected={connected?.map((item) => item.displayLabel) ?? []}
          busyProvider={busyProvider}
          message={message}
          onConnect={(provider) => void connect(provider)}
          onSkip={() => void skip()}
        />
      )}
    </BloomMenu>
  );
}

function ProviderStep(props: {
  providers: PsychSetupStatus["providers"];
  connected: string[];
  busyProvider: WearableProvider | null;
  message: string | null;
  onConnect: (provider: WearableProvider) => void;
  onSkip: () => void;
}) {
  return (
    <div className="p-3" data-bloom-step="choose-wearable">
      <p className="text-[11px] leading-5 text-[var(--fintheon-muted)]">
        Connect a wearable so ER begins from your current state.
      </p>
      {props.connected.length ? (
        <p className="mt-3 text-[10px] text-[var(--fintheon-muted)]">
          Connected: {props.connected.join(", ")}
        </p>
      ) : null}
      <div className="mt-3 space-y-1">
        {props.providers.map((provider) => (
          <button
            key={provider.id}
            type="button"
            disabled={
              !provider.configured || props.busyProvider === provider.id
            }
            onClick={() => props.onConnect(provider.id)}
            className="flex w-full items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-[var(--fintheon-accent)]/8 disabled:cursor-not-allowed disabled:opacity-45"
          >
            <span className="flex min-w-0 items-center gap-2">
              <Activity className="h-3.5 w-3.5 shrink-0 text-[var(--fintheon-accent)]" />
              <span>
                <span className="block text-xs font-medium text-[var(--fintheon-text)]">
                  {provider.label}
                </span>
                <span className="block text-[10px] text-[var(--fintheon-muted)]">
                  {provider.configured
                    ? "Heart data provider"
                    : "Not configured"}
                </span>
              </span>
            </span>
            <ExternalLink className="h-3.5 w-3.5 shrink-0 text-[var(--fintheon-muted)]" />
          </button>
        ))}
      </div>
      {props.message ? (
        <p className="mt-3 text-[10px] text-[var(--fintheon-muted)]">
          {props.message}
        </p>
      ) : null}
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={props.onSkip}
          className="rounded-lg px-2.5 py-1.5 text-[10px] text-[var(--fintheon-muted)] transition-colors hover:text-[var(--fintheon-text)]"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}

function ApprovalStep(props: {
  provider: WearableProvider | null;
  message: string | null;
  onBack: () => void;
  onRecheck: () => void;
}) {
  return (
    <div className="p-3" data-bloom-step="wearable-approval">
      <p className="text-sm font-medium text-[var(--fintheon-text)]">
        Approve {props.provider ?? "your wearable"}
      </p>
      <p className="mt-1 text-[11px] leading-5 text-[var(--fintheon-muted)]">
        Complete the approval in the provider window, then confirm it here.
      </p>
      {props.message ? (
        <p className="mt-3 text-[10px] text-[var(--fintheon-muted)]">
          {props.message}
        </p>
      ) : null}
      <div className="mt-5 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={props.onBack}
          className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[10px] text-[var(--fintheon-muted)] transition-colors hover:text-[var(--fintheon-text)]"
        >
          <ArrowLeft size={12} /> Back
        </button>
        <button
          type="button"
          onClick={props.onRecheck}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--fintheon-accent)] px-3 py-1.5 text-[10px] font-medium text-[var(--fintheon-bg)]"
        >
          <RefreshCw size={12} /> Check connection
        </button>
      </div>
    </div>
  );
}
